import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Loader2, Sparkles, Save, Printer, Award } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { PrintFooter } from "@/lib/brand";

const Section = ({ s, onSaved }) => {
  const [f, setF] = useState({ notes: s.notes || "", draft: s.draft || "" });
  const [tip, setTip] = useState("");
  const [busy, setBusy] = useState("");
  const save = async (body = f) => {
    setBusy("save");
    try { await http.put(`/pathway/plan/${s.id}`, body); onSaved(s.id, body); toast.success(`${s.title} saved`); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const assist = async () => {
    setBusy("assist");
    try {
      const r = await http.post(`/pathway/plan/${s.id}/assist`, f);
      const body = { ...f, draft: r.data.draft };
      setF(body); setTip(r.data.tip);
      await http.put(`/pathway/plan/${s.id}`, body); onSaved(s.id, body);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const input = "w-full px-4 py-3 rounded-2xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm";
  return (
    <article className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6 no-print" data-testid={`plan-section-${s.id}`}>
      <h3 className="font-display text-xl text-[#2C4A3E]">{s.title}</h3>
      <p className="text-xs text-stone-500 mt-1">{s.hint}</p>
      <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} maxLength={4000} placeholder="Your rough ideas, in your own words…" data-testid={`plan-notes-${s.id}`} className={`${input} mt-4`} />
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={assist} disabled={!!busy || f.notes.trim().length < 3} data-testid={`plan-assist-${s.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C87D53] text-white text-sm disabled:opacity-50">
          {busy === "assist" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Help me write this</button>
      </div>
      {(f.draft || tip) && (
        <>
          <textarea value={f.draft} onChange={(e) => setF({ ...f, draft: e.target.value })} rows={6} maxLength={6000} data-testid={`plan-draft-${s.id}`} className={`${input} mt-4 font-mono text-[13px]`} />
          {tip && <p className="text-sm italic text-[#6B9080] mt-2" data-testid={`plan-tip-${s.id}`}>Coach: {tip}</p>}
        </>
      )}
      <button onClick={() => save()} disabled={!!busy} data-testid={`plan-save-${s.id}`} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-50">
        {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save section</button>
    </article>
  );
};

const PrintPlan = ({ plan }) => (
  <div className="hidden print:block" data-testid="plan-print">
    <h1 className="font-display text-4xl">{plan.student}'s Business Plan</h1>
    <p className="text-sm">{plan.state} · ConsciousLearn Business Launch Track</p>
    {plan.sections.map((s) => (
      <section key={s.id} className="mt-6 break-inside-avoid"><h2 className="font-display text-2xl">{s.title}</h2>
        <div className="lesson-md"><ReactMarkdown>{s.draft || s.notes || "_Not written yet_"}</ReactMarkdown></div></section>
    ))}
    <PrintFooter />
  </div>
);

export default function BusinessPlan() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { http.get("/pathway/plan").then((r) => setPlan(r.data)).catch((e) => setErr(formatApiError(e))); }, []);
  if (err) return <div className="max-w-4xl mx-auto px-6 py-20 text-stone-600" data-testid="plan-error">{err}</div>;
  if (!plan) return <div className="max-w-4xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  const filled = plan.sections.filter((s) => (s.draft || s.notes || "").trim()).length;
  const onSaved = (id, body) => setPlan((p) => ({ ...p, sections: p.sections.map((s) => (s.id === id ? { ...s, ...body } : s)) }));
  const certify = async () => {
    setBusy(true);
    try { const r = await http.post("/pathway/certificate"); nav(`/certificate/${r.data.id}`); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="business-plan-page">
      <div className="no-print">
        <Link to="/pathway" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="plan-back-link"><ArrowLeft className="w-4 h-4" /> Career Pathway</Link>
        <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-6">My Business Plan</h1>
        <p className="text-stone-600 mt-3">Write rough notes, then let your coach polish them, always in your own words. Coach drafts save automatically; click Save after your own edits. <b data-testid="plan-filled-count">{filled}/{plan.sections.length}</b> sections done.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={() => window.print()} data-testid="plan-print-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E6DEC3] text-sm text-[#2C4A3E] hover:bg-[#F1EADB]"><Printer className="w-4 h-4" /> Print / Save as PDF</button>
          <button onClick={certify} disabled={busy} data-testid="plan-certificate-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D99B26] text-white text-sm disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Get my certificate</button>
        </div>
        <div className="space-y-5 mt-10">{plan.sections.map((s) => <Section key={s.id} s={s} onSaved={onSaved} />)}</div>
      </div>
      <PrintPlan plan={plan} />
    </div>
  );
}
